import { useEffect, useMemo, useRef, useState } from "react";
import { Star, PenLine, BadgeCheck, ImagePlus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const Stars = ({ value, testPrefix }) => (
  <div className="flex items-center gap-0.5" data-testid={`${testPrefix}-stars`} aria-label={`${value} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n} className={`w-4 h-4 ${n <= value ? "star-gold" : "text-gold-line"}`} />
    ))}
  </div>
);

export function ReviewsSection() {
  const [reviews, setReviews] = useState([]);
  const [name, setName] = useState("");
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [productName, setProductName] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const [photoBusy, setPhotoBusy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
  }, []);

  const average = useMemo(() => {
    if (!reviews.length) return 0;
    return Math.round((reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length) * 10) / 10;
  }, [reviews]);

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/reviews/photo", fd);
      setPhotoUrl(r.data.url);
      toast.success("Photo attached");
    } catch (err) {
      toast.error(formatApiError(err, "Could not attach that photo"));
    } finally {
      setPhotoBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !comment.trim()) return toast.error("Please add your name and a short review");
    setSubmitting(true);
    try {
      await api.post("/reviews", { name: name.trim(), rating, comment: comment.trim(), product_name: productName.trim(), photo_url: photoUrl });
      toast.success("Thank you! Your review will appear once approved.");
      setName("");
      setComment("");
      setProductName("");
      setPhotoUrl("");
      setRating(5);
    } catch (err) {
      toast.error(formatApiError(err, "Could not submit your review. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="reviews" data-testid="reviews-section" className="border-y border-gold-line bg-ivory-alt scroll-mt-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-12 sm:py-16">
        <div className="text-center mb-10">
          <p className="font-accent text-xs tracking-[0.3em] uppercase text-gold-text mb-2">Customer Love</p>
          <h2 data-testid="reviews-heading" className="font-display text-3xl sm:text-4xl font-semibold text-maroon-dark tracking-tight">
            Ratings &amp; Reviews
          </h2>
          <div className="flex items-center justify-center gap-3 mt-4">
            <span className="hairline-gold w-16" />
            <span className="diamond-sep" />
            <span className="hairline-gold w-16" />
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 items-start">
          <div data-testid="reviews-summary" className="luxury-card rounded-3xl border border-gold-line bg-white p-6 sm:p-8 text-center lg:sticky lg:top-24 self-start">
            <p data-testid="reviews-average" className="font-display text-6xl font-semibold text-maroon-deep">{average || "–"}</p>
            <div className="flex justify-center mt-3"><Stars value={Math.round(average)} testPrefix="reviews-average" /></div>
            <p className="text-sm text-ink-muted mt-3">{reviews.length} customer {reviews.length === 1 ? "review" : "reviews"}</p>
            <p className="text-xs text-ink-muted/80 mt-1">on our sarees, kurtis and service</p>

            <form data-testid="write-review-form" onSubmit={submit} className="mt-8 space-y-3 text-left border-t border-gold-line pt-6">
              <p className="flex items-center gap-2 text-sm font-semibold text-ink"><PenLine className="w-4 h-4 text-gold-text" /> Write a Review</p>
              <div>
                <Input data-testid="write-review-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="border-gold-line" />
              </div>
              <div className="flex items-center gap-1.5" data-testid="star-picker">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" data-testid={`star-picker-star-${n}`} onClick={() => setRating(n)} aria-label={`Rate ${n} star${n > 1 ? "s" : ""}`} className="p-0.5">
                    <Star className={`w-7 h-7 transition-transform duration-150 hover:scale-110 ${n <= rating ? "star-gold" : "text-gold-line"}`} />
                  </button>
                ))}
              </div>
              <Textarea data-testid="write-review-comment-input" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="How was the fabric, fit and service?" rows={3} className="border-gold-line" />
              <Input data-testid="write-review-product-input" value={productName} onChange={(e) => setProductName(e.target.value)} placeholder="Which piece? (optional)" className="border-gold-line" />

              <div>
                {photoUrl ? (
                  <div className="flex items-center gap-3 rounded-xl border border-gold-line bg-ivory/70 p-2">
                    <img data-testid="write-review-photo-preview" src={fileUrl(photoUrl)} alt="Attached" className="h-14 w-12 rounded-lg object-cover" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-ink">Photo attached</p>
                      <p className="text-[11px] text-ink-muted">Customers will see it with your review</p>
                    </div>
                    <button type="button" data-testid="write-review-photo-remove" onClick={() => setPhotoUrl("")} title="Remove photo" className="p-2 rounded-full text-ink-muted hover:text-red-600 hover:bg-red-50 transition-colors duration-200">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label
                    htmlFor="review-photo-upload"
                    data-testid="write-review-photo-dropzone"
                    className={`flex items-center gap-3 rounded-xl border border-dashed border-gold-line bg-ivory/50 px-4 py-3 cursor-pointer hover:border-gold transition-colors duration-200 ${photoBusy ? "opacity-60 pointer-events-none" : ""}`}
                  >
                    {photoBusy ? <ImagePlus className="w-5 h-5 text-maroon animate-bounce" /> : <ImagePlus className="w-5 h-5 text-gold-text" />}
                    <span className="text-xs text-ink-muted">{photoBusy ? "Attaching..." : "Add a photo of your outfit (optional)"}</span>
                  </label>
                )}
                <input data-testid="write-review-photo-input" id="review-photo-upload" ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPhoto} />
              </div>

              <Button data-testid="write-review-submit-btn" type="submit" disabled={submitting || photoBusy} className="w-full rounded-full bg-maroon hover:bg-maroon-dark text-white py-5 font-semibold disabled:opacity-50">
                {submitting ? "Sending..." : "Submit Review"}
              </Button>
              <p className="text-[11px] text-ink-muted/80 text-center">Reviews appear after a quick check by our team.</p>
            </form>
          </div>

          <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4">
            {reviews.length === 0 ? (
              <div data-testid="reviews-empty" className="sm:col-span-2 flex flex-col items-center gap-2 py-14 text-center border border-dashed border-gold-line rounded-3xl bg-white/70">
                <BadgeCheck className="w-9 h-9 text-gold" />
                <p className="font-display text-2xl text-maroon-dark">Be the first to review</p>
                <p className="text-sm text-ink-muted max-w-sm">Ordered from us? Share your experience — it helps other shoppers and means a lot to our small team.</p>
              </div>
            ) : (
              reviews.map((r, i) => (
                <article key={r.id} data-testid={`review-card-${i}`} className={`luxury-card flex flex-col gap-3 rounded-2xl border border-gold-line bg-white p-5 sm:p-6 ${r.photo_url ? "sm:col-span-2" : ""}`}>
                  {r.photo_url ? (
                    <div className="grid sm:grid-cols-3 gap-4 items-start">
                      <img data-testid={`review-photo-${i}`} src={fileUrl(r.photo_url)} alt={`${r.name}'s photo`} className="w-full h-48 sm:h-44 object-cover rounded-xl border border-gold-line" />
                      <div className="sm:col-span-2 flex flex-col gap-3">
                        <Stars value={r.rating} testPrefix={`review-card-${i}`} />
                        <p className="font-display text-base sm:text-lg leading-relaxed text-ink italic">“{r.comment}”</p>
                        <div className="mt-auto flex items-center justify-between gap-2 pt-1">
                          <div>
                            <p data-testid={`review-name-${i}`} className="text-sm font-semibold text-maroon-deep">{r.name}</p>
                            {r.product_name ? <p className="text-[11px] text-ink-muted">{r.product_name}</p> : null}
                          </div>
                          <p className="text-[11px] text-ink-muted/70">{new Date(r.created_at).toLocaleDateString("en-IN", { month: "short", year: "numeric" })}</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <Stars value={r.rating} testPrefix={`review-card-${i}`} />
                      <p className="font-display text-base sm:text-lg leading-relaxed text-ink italic">“{r.comment}”</p>
                      <div className="mt-auto flex items-center justify-between gap-2 pt-1">
                        <div>
                          <p data-testid={`review-name-${i}`} className="text-sm font-semibold text-maroon-deep">{r.name}</p>
                          {r.product_name ? <p className="text-[11px] text-ink-muted">{r.product_name}</p> : null}
                        </div>
                        <p className="text-[11px] text-ink-muted/70">{new Date(r.created_at).toLocaleDateString("en-IN", { month: "short", year: "numeric" })}</p>
                      </div>
                    </>
                  )}
                </article>
              ))
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
